import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Skeleton,
  Typography,
  Result,
  Button,
  Menu,
  Layout,
  Drawer,
  Select,
  Tag,
} from "antd";
import { useHistory, Link } from "react-router-dom";
import { MenuOutlined } from "@ant-design/icons";
import { decodeStr } from "../../utils";
import "./OrgDetailsV2.css";
import useOrgDetails from "./hooks/useOrgDetails";
// import useOrgModules from "./hooks/useOrgModules";

// Import tab components
import OrgUsage from "./tabs/OrgUsage";
// import OrgFatture from "./tabs/OrgFatture";
import OrgUsers from "./tabs/OrgUsers";
import OrgGiotto from "./tabs/OrgGiotto";
import OrgAnnunciFunebri from "./tabs/OrgAnnunciFunebri";
import OrgDocumenti from "./tabs/OrgDocumenti";
import OrgTimeline from "./tabs/OrgTimeline";
import OrgFatturazioneElettronica from "./tabs/OrgFatturazioneElettronica";
import OrgZoho from "./tabs/OrgZoho";
import OrgCentroPrenotazioni from "./tabs/OrgCentroPrenotazioni";
import OrgJson from "./tabs/OrgJson";
import OrgRiepilogo from "./tabs/OrgRiepilogo";
import OrgFeatures from "./tabs/OrgFeatures";
import OrgOffices from "./tabs/OrgOffices";
import OrgSettings from "./tabs/OrgSettings";
import OrgDocScanner from "./tabs/OrgDocScanner";
import OrgAbbonamento from "./tabs/OrgAbbonamento";
import OrgAllegati from "./tabs/OrgAllegati";
import OrgStorage from "./tabs/OrgStorage";
import GiottoAiHub from "../shared/GiottoAiHub";

const { Title } = Typography;
const { Sider, Content } = Layout;
const { Option, OptGroup } = Select;

const LEGACY_AI_TAB_VIEWS = {
  crediti: "credits",
  "ai-results": "results",
};

const OrgDetailsV2 = (props) => {
  const { orgId } = props.match.params;
  const { org, loading: orgLoading } = useOrgDetails(orgId);
  // const { loading: modulesLoading } = useOrgModules(orgId);
  const history = useHistory();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [openKeys, setOpenKeys] = useState([]);
  const selectRef = useRef(null);

  // Menu items using modern Ant Design items array pattern
  const menuItems = useMemo(() => 
    org
      ? [
          {
            key: "anagrafica",
            label: "Anagrafica",
            children: [
              { key: "riepilogo", label: "Riepilogo" },
              { key: "abbonamento", label: "Moduli" },
              { key: "users", label: "Utenti" },
              { key: "offices", label: "Aziende & Sedi" },
            ],
          },
          {
            key: "configurazioni",
            label: "Configurazioni",
            children: [
              { key: "settings", label: "Impostazioni" },
              { key: "features", label: "Funzionalità" },
              { key: "documenti", label: "Documenti" },
              { key: "centro-prenotazioni", label: "Centro Prenotazioni" },
            ],
          },
          {
            key: "utilizzo",
            label: "Utilizzo",
            children: [
              
              { key: "usage", label: "Conteggi" },
              { key: "storage", label: "Storage" },
              { key: "timeline-operazioni", label: "Timeline" },
              { key: "allegati", label: "Allegati" },
              { key: "giotto", label: "Giotto" },
              { key: "doc-scanner", label: "Doc Scanner" },
              { key: "annunci-funebri", label: "Annunci Funebri" },
              { key: "ai", label: "Giotto AI" },
            ],
          },
          {
            key: "integrazioni",
            label: "Integrazioni",
            children: [
              { key: "fatturazione-elettronica", label: "Aruba" },
              { key: "zoho", label: "Zoho" },
            ],
          },          
          {
            key: "altro",
            label: "Altro",
            children: [{ key: "json", label: "JSON" }],
          },
        ]
      : []
  , [org]);

  // Flatten menu items for mobile dropdown and include nested items
  const flatMenuItems = useMemo(() => {
    const items = [];
    menuItems.forEach((group) => {
      if (group.children) {
        group.children.forEach((item) => {
          items.push(item);
          // If item has children (nested menu), add those too
          if (item.children) {
            items.push(...item.children);
          }
        });
      }
    });
    return items;
  }, [menuItems]);

  // Function to check if a tab key is valid
  const isValidTab = useCallback((tabKey) => {
    return flatMenuItems.some((item) => item.key === tabKey);
  }, [flatMenuItems]);

  // Initialize activeTab state - use URL param if available, validation will happen after org loads
  const initialTab = LEGACY_AI_TAB_VIEWS[props.match.params.tabName]
    ? "ai"
    : props.match.params.tabName || "riepilogo";
  const [activeTab, setActiveTab] = useState(initialTab);

  // Find which group contains the active tab and keep it open
  useEffect(() => {
    const keysToOpen = [];
    
    // Find the top-level group
    const activeGroup = menuItems.find((group) => {
      if (group.children) {
        return group.children.some((item) => {
          // Check if the active tab is directly in this group
          if (item.key === activeTab) {
            return true;
          }
          // Check if the active tab is in a nested submenu
          if (item.children) {
            const hasNestedActive = item.children.some((nested) => nested.key === activeTab);
            if (hasNestedActive) {
              keysToOpen.push(item.key); // Also open the submenu
            }
            return hasNestedActive;
          }
          return false;
        });
      }
      return false;
    });
    
    if (activeGroup) {
      keysToOpen.unshift(activeGroup.key); // Add the main group key at the beginning
      setOpenKeys(keysToOpen);
    }
  }, [activeTab, menuItems]);

  // Redirect to default tab if no tabName is provided
  useEffect(() => {
    if (!props.match.params.tabName) {
      try {
        // Use a safer approach with a setTimeout to avoid immediate navigation
        setTimeout(() => {
          history.replace(`/orgs/${orgId}/riepilogo`);
        }, 0);
      } catch (error) {
        console.error("Navigation error:", error);
      }
    }
  }, [orgId, history, props.match.params.tabName, isValidTab]);

  // Update active tab when URL changes
  useEffect(() => {
    // Don't validate tabs until org data is loaded
    if (!org) return;

    const legacyAiView = LEGACY_AI_TAB_VIEWS[props.match.params.tabName];
    if (legacyAiView) {
      const params = new URLSearchParams(props.location.search);
      params.set("view", legacyAiView);
      setActiveTab("ai");
      history.replace(`/orgs/${orgId}/ai?${params.toString()}`);
      return;
    }

    if (props.match.params.tabName && isValidTab(props.match.params.tabName)) {
      setActiveTab(props.match.params.tabName);
    } else {
      setActiveTab("riepilogo");
      // Redirect to default tab if the tab in URL is invalid
      if (props.match.params.tabName && !isValidTab(props.match.params.tabName)) {
        try {
          // Use a safer approach with a setTimeout to avoid immediate navigation
          setTimeout(() => {
            history.replace(`/orgs/${orgId}/riepilogo`);
          }, 0);
        } catch (error) {
          console.error("Navigation error:", error);
        }
      }
    }
  }, [
    props.location.search,
    props.match.params.tabName,
    orgId,
    history,
    flatMenuItems,
    org,
    isValidTab,
  ]);

  // Track visited org in localStorage for dashboard recent orgs
  useEffect(() => {
    if (!org || !orgId) return;
    try {
      const key = "dashboard-recent-orgs";
      const stored = JSON.parse(localStorage.getItem(key) || "[]");
      const filtered = stored.filter((o) => o.id !== orgId);
      filtered.unshift({ id: orgId, name: org.nome || orgId, stato: org.stato, tipo_istanza: org.tipo_istanza, visitedAt: new Date().toISOString() });
      localStorage.setItem(key, JSON.stringify(filtered.slice(0, 20)));
    } catch (e) { /* ignore */ }
  }, [org, orgId]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle menu item click
  const handleMenuClick = (e) => {
    setActiveTab(e.key);
    try {
      setTimeout(() => {
        history.push(`/orgs/${orgId}/${e.key}`);
      }, 0);
    } catch (error) {
      console.error("Navigation error:", error);
    }
    if (isMobile) {
      setDrawerVisible(false);
    }
  };

  // Handle dropdown change for mobile
  const handleDropdownChange = (value) => {
    setActiveTab(value);
    // Blur the select to close it and hide mobile keyboard
    if (selectRef.current) {
      selectRef.current.blur();
    }
    try {
      setTimeout(() => {
        history.push(`/orgs/${orgId}/${value}`);
      }, 0);
    } catch (error) {
      console.error("Navigation error:", error);
    }
  };

  // Handle submenu open/close
  const handleOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  // Mostra lo skeleton solo se entrambi i dati principali stanno caricando
  if (orgLoading) {
    return <Skeleton active />;
  }

  if (!org) {
    return (
      <div className="org-details-v2-container">
        <Result
          status="404"
          title="Organizzazione non trovata"
          subTitle="L'organizzazione richiesta non esiste o è stata rimossa."
          extra={
            <Button type="primary">
              <Link to="/orgs">Torna alla lista clienti</Link>
            </Button>
          }
        />
      </div>
    );
  }

  // Render the active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case "riepilogo":
        return <OrgRiepilogo org={org} />;
      case "abbonamento":
        return <OrgAbbonamento orgId={orgId} />;
      case "usage":
        return <OrgUsage orgId={orgId} />;
      case "storage":
        return <OrgStorage orgId={orgId} />;
      
      // case "fatture":
      //   return <OrgFatture org={org} />;
      case "users":
        return <OrgUsers orgId={orgId} />;
      case "offices":
        return <OrgOffices orgId={orgId} />;
      case "settings":
        return <OrgSettings org={org} />;
      case "features":
        return <OrgFeatures orgId={orgId} org={org} />;
      case "ai":
        return <GiottoAiHub mode="org" orgId={orgId} />;
      case "giotto":
        return <OrgGiotto orgId={orgId} />;
      case "doc-scanner":
        return <OrgDocScanner orgId={orgId} />;
      case "annunci-funebri":
        return <OrgAnnunciFunebri orgId={orgId} />;
      case "allegati":
        return <OrgAllegati orgId={orgId} />;
      case "documenti":
        return <OrgDocumenti orgId={orgId} />;
      case "timeline-operazioni":
        return <OrgTimeline orgId={orgId} />;
      case "fatturazione-elettronica":
        return <OrgFatturazioneElettronica orgId={orgId} />;
      case "zoho":
        return <OrgZoho orgId={orgId} />;
      case "centro-prenotazioni":
        return <OrgCentroPrenotazioni orgId={orgId} orgNome={org?.nome} />;
      case "json":
        return <OrgJson org={org} />;
      default:
        return <OrgRiepilogo org={org} />;
    }
  };

  return (
    <div className="org-details-v2-container">
      <div className="org-header">
        <div className="org-header-title">
          {org.logo?.url && (
            <img 
              src={org.logo.url} 
              alt={`${org.nome} logo`}
              className="org-header-logo"
            />
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Title
              level={2}
              style={{
                margin: 0,
                color: org?.stato ? (org.stato === 'attiva' ? 'green' : 'red') : undefined,
              }}
            >
              {decodeStr(org.nome)}
            </Title>
            {org?.stato && (
              <Tag color={org.stato === 'attiva' ? 'green' : 'red'} style={{ margin: 0, height: 24, display: 'inline-flex', alignItems: 'center' }}>
                {org.stato === 'attiva' ? 'ATTIVA' : (String(org.stato).toUpperCase() || 'NON ATTIVA')}
              </Tag>
            )}
          </div>
        </div>

        {isMobile && (
          <div className="mobile-tab-selector">
            <Select
              ref={selectRef}
              value={activeTab}
              onChange={handleDropdownChange}
              style={{ width: "100%", marginBottom: "16px" }}
              showSearch
              optionFilterProp="children"
              placeholder="Seleziona sezione"
            >
              {menuItems.map((group) => (
                <OptGroup key={group.key} label={group.label}>
                  {group.children?.map((item) =>
                    item.children ? (
                      // Nested submenu items (like AI)
                      item.children.map((nested) => (
                        <Option key={nested.key} value={nested.key}>
                          {item.label} › {nested.label}
                        </Option>
                      ))
                    ) : (
                      <Option key={item.key} value={item.key}>
                        {item.label}
                      </Option>
                    )
                  )}
                </OptGroup>
              ))}
            </Select>
          </div>
        )}
      </div>

      <Layout className="org-details-layout">
        {!isMobile ? (
          <Sider width={250} className="org-details-sider" theme="light">
            <Menu
              mode="inline"
              selectedKeys={[activeTab]}
              openKeys={openKeys}
              onOpenChange={handleOpenChange}
              onClick={handleMenuClick}
              items={menuItems}
              style={{ height: "100%" }}
            />
          </Sider>
        ) : (
          <div className="mobile-menu-button">
            <Button
              type="primary"
              icon={<MenuOutlined />}
              onClick={() => setDrawerVisible(true)}
              style={{ marginBottom: "16px" }}
            >
              Menu
            </Button>
            <Drawer
              title="Menu"
              placement="left"
              onClose={() => setDrawerVisible(false)}
              open={drawerVisible}
            >
              <Menu
                mode="inline"
                selectedKeys={[activeTab]}
                openKeys={openKeys}
                onOpenChange={handleOpenChange}
                onClick={handleMenuClick}
                items={menuItems}
                style={{ border: "none" }}
              />
            </Drawer>
          </div>
        )}
        <Content className="org-details-content">{renderTabContent()}</Content>
      </Layout>
    </div>
  );
};

export default OrgDetailsV2;
